import type { CSSProperties, ReactNode } from 'react'

import cn from '@config/tailwind/cn'

interface MarqueeProps {
  /** One set of items. */
  children: ReactNode
  /** Seconds for one loop. */
  duration?: number
  reverse?: boolean
  /** Sets in each half of the loop; raise it when one set is narrower than a wide screen. */
  setsPerHalf?: number
  className?: string
}

/**
 * A row that scrolls forever and pauses on hover. The track holds two identical halves and moves by
 * one half (`landing-marquee` in globals.css), so the loop has no seam. Every copy after the first
 * set is hidden from screen readers. Stops with reduced motion.
 */
export function Marquee({ children, duration = 60, reverse, setsPerHalf = 1, className }: MarqueeProps) {
  const sets = Array.from({ length: setsPerHalf * 2 }, (_, index) => index)

  return (
    <div className={cn('group w-full overflow-hidden', className)}>
      <div
        className={cn(
          'flex w-max gap-4 group-hover:[animation-play-state:paused] motion-reduce:animate-none',
          'animate-[landing-marquee_var(--marquee-duration)_linear_infinite]',
          reverse && '[animation-direction:reverse]',
        )}
        style={{ '--marquee-duration': `${duration}s` } as CSSProperties}
      >
        {sets.map((index) => (
          <div key={index} className='flex shrink-0 gap-4' aria-hidden={index > 0 ? true : undefined}>
            {children}
          </div>
        ))}
      </div>
    </div>
  )
}
